import React, { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_PW_KEY = "rss_admin_pw";
const POLL_MS = 15_000;

/**
 * Floating "live viewers" pill, only renders if the admin password is stored
 * in localStorage (set by /admin login). Polls /api/admin/viewers every 15s.
 * If the request 401s the admin token is cleared and the pill hides itself.
 */
export const LiveViewerPill = () => {
  const [count, setCount] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    typeof window !== "undefined" ? !!localStorage.getItem(ADMIN_PW_KEY) : false,
  );

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    const fetchCount = async () => {
      const pw = localStorage.getItem(ADMIN_PW_KEY);
      if (!pw) return;
      try {
        const r = await fetch(`${API}/admin/viewers`, {
          headers: { "X-Admin-Password": pw },
        });
        if (r.status === 401) {
          localStorage.removeItem(ADMIN_PW_KEY);
          setIsAdmin(false);
          return;
        }
        if (!r.ok) throw new Error("bad status");
        const data = await r.json();
        if (!cancelled) setCount(data.count);
      } catch {
        /* keep last known count */
      }
    };
    fetchCount();
    const id = setInterval(fetchCount, POLL_MS);
    // Watch for admin login/logout in another tab
    const onStorage = (e) => {
      if (e.key === ADMIN_PW_KEY) setIsAdmin(!!e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("storage", onStorage);
    };
  }, [isAdmin]);

  const signOut = () => {
    localStorage.removeItem(ADMIN_PW_KEY);
    setIsAdmin(false);
  };

  if (!isAdmin || hidden) return null;

  return (
    <div
      data-testid="live-viewers-pill"
      className="fixed bottom-16 right-4 z-50 flex items-center gap-2 border border-[#FFC800] bg-black/85 backdrop-blur-md px-3 py-2 shadow-lg"
      style={{ fontFamily: '"JetBrains Mono", monospace' }}
    >
      <Eye size={14} className="text-[#FFC800]" />
      <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-400">Live</div>
      <div
        className="text-base font-black text-white tabular-nums"
        style={{ fontFamily: '"Barlow Condensed", sans-serif' }}
        data-testid="live-viewers-count"
      >
        {count === null ? "—" : count}
      </div>
      <button
        onClick={signOut}
        title="Sign out admin"
        data-testid="live-viewers-signout"
        className="text-neutral-500 hover:text-white ml-1"
      >
        <X size={12} />
      </button>
    </div>
  );
};

export default LiveViewerPill;
